import {
  EmptyState,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  StyledSearchBar,
  useDebouncedSearch,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useStyles } from '../../../hooks/useStyles';
import {
  jobActivitiesIdsState,
  jobActivitiesPageState,
  jobActivitiesState,
  loadingJobActivitiesState,
  searchJobActivitiesState,
  selectedJobActivityIdState,
} from '../../../storage/jobTypesStates';
import { FloatingCardContent } from '../../layout/cards/FloatingCardContent';
import StyledTooltip from '../../ui/StyledTooltip';
import { JobActivityListItem } from './JobActivityListItem';

export function JobActivitiesList() {
  const { theme, classes, css } = useStyles();
  const jobActivitiesIds = useRecoilValue(jobActivitiesIdsState);
  const loadingActivities = useRecoilValue(loadingJobActivitiesState);
  const { search, setSearch } = useDebouncedSearch(useRecoilState(searchJobActivitiesState));
  const paginatedActivities = useRecoilValue(jobActivitiesState);
  const [jobActivitiesPage, setActivitiesPage] = useRecoilState(jobActivitiesPageState);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const setSelectedActivity = useSetRecoilState(selectedJobActivityIdState);

  const onUpdate = () => {
    setActivitiesPage(0);
    setSelectedActivity(undefined);
  };

  useEffect(() => {
    if (!scrollableRef.current) return;
    if (jobActivitiesPage === 0) {
      const scrollableDiv = scrollableRef.current;
      scrollableDiv.scrollTo(0, 0);
    }
  }, [jobActivitiesPage, scrollableRef]);

  return (
    <FloatingCardContent
      flexible
      gap={theme.sizing.scale500}
    >
      {!jobActivitiesIds && !loadingActivities && <EmptyState title="No cuentas con ningúna actividad." />}
      {!jobActivitiesIds && loadingActivities && <Loading />}
      {jobActivitiesIds && (
        <>
          <FlexRow gap={theme.sizing.scale300}>
            <div className={classes.flexible(1)}>
              <StyledSearchBar
                name="activity-search"
                value={search}
                onChange={setSearch}
                clearable
                noMargin
              />
            </div>
            <FlexRow
              gap={0}
              classNames={css({ alignItems: 'center' })}
            >
              <StyledTooltip
                content={'Actualizar'}
                showArrow={false}
              >
                <Button
                  size="mini"
                  shape="square"
                  onClick={onUpdate}
                  kind="tertiary"
                >
                  <MaterialIcon name={'sync'} />
                </Button>
              </StyledTooltip>
            </FlexRow>
          </FlexRow>
          {!!paginatedActivities?.items.length && (
            <div
              id="scrollableDiv"
              className={css({ height: '100%', overflow: 'auto', marginBottom: '12px' })}
              ref={scrollableRef}
            >
              <InfiniteScroll
                dataLength={paginatedActivities?.items.length}
                next={() => setActivitiesPage(jobActivitiesPage + 1)}
                hasMore={paginatedActivities?.hasNext ?? false}
                loader={
                  <div className={css({ height: '80px', overflow: 'hidden' })}>
                    <Loading />
                  </div>
                }
                endMessage={
                  paginatedActivities?.hasNext && (
                    <p style={{ textAlign: 'center' }}>
                      <b>Cargando...</b>
                    </p>
                  )
                }
                scrollableTarget="scrollableDiv"
              >
                <FlexColumn gap={0}>
                  {jobActivitiesIds?.map((activityId) => (
                    <JobActivityListItem
                      key={activityId}
                      activityId={activityId}
                    />
                  ))}
                </FlexColumn>
              </InfiniteScroll>
            </div>
          )}
          {!jobActivitiesIds?.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
        </>
      )}
    </FloatingCardContent>
  );
}
